import { useState, useEffect, useRef } from "react";

// ─── QUESTION DATA ────────────────────────────────────────────────────────────
const QUESTIONS = [
  {
    id: 1,
    text: "\"Novation\" is the legal process by which:",
    options: [
      "A trader cancels an order before it is filled.",
      "The CDCC interposes itself between the buyer and seller, becoming the counterparty to both.",
      "A firm increases its margin requirements.",
      "An option is adjusted for a stock split.",
    ],
    correct: 1,
    rationale: "Novation eliminates counterparty risk by replacing a single contract between two traders with two separate contracts with the clearinghouse.",
    type: "concept",
  },
  {
    id: 2,
    text: "On the Montreal Exchange (MX), a \"Market Order\" is an order to:",
    options: [
      "Buy or sell at the best available price currently in the market.",
      "Buy or sell only at a specific price or better.",
      "Buy or sell only during the opening rotation.",
      "Execute a trade only if the volume is over 100 contracts.",
    ],
    correct: 0,
    rationale: "Market orders prioritize speed of execution over price certainty.",
    type: "concept",
  },
  {
    id: 3,
    text: "An investor exercises a call option on ABC stock. If the exercise occurs on Tuesday, on which day must the shares be delivered under the Canadian T+1 settlement cycle?",
    options: ["Tuesday", "Wednesday", "Thursday", "Friday"],
    correct: 1,
    rationale: "Canadian equity options now follow a T+1 settlement cycle for the resulting stock delivery.",
    type: "numerical",
  },
  {
    id: 4,
    text: "A trader enters a \"Stop-Limit\" order to sell at $45, with a limit of $44.50. The market price drops quickly from $46 to $44.25. What happens to the order?",
    options: [
      "It is filled at $44.25.",
      "It is triggered at $45 and becomes a market order.",
      "It is triggered at $45 but remains unfilled because the price is below the $44.50 limit.",
      "The exchange cancels the order automatically.",
    ],
    correct: 2,
    rationale: "A stop-limit order becomes a limit order once the stop price is hit. If the market \"gaps\" below the limit, the trade will not execute.",
    type: "case",
  },
  {
    id: 5,
    text: "Which entity is responsible for the daily \"Mark-to-Market\" process for futures in Canada?",
    options: [
      "The individual Registered Representative.",
      "The Canadian Derivatives Clearing Corporation (CDCC).",
      "The Bank of Canada.",
      "The Toronto Stock Exchange.",
    ],
    correct: 1,
    rationale: "The CDCC manages the daily settlement of gains and losses to ensure system liquidity.",
    type: "concept",
  },
  {
    id: 6,
    text: "An \"Equity Option\" that is exercised results in ______ settlement, while an \"Index Option\" results in ______ settlement.",
    options: ["Cash; Physical", "Physical; Cash", "Physical; Physical", "Cash; Cash"],
    correct: 1,
    rationale: "Equity options involve actual shares; index options settle in cash because delivering a proportional slice of 60 stocks is impractical.",
    type: "concept",
  },
  {
    id: 7,
    text: "The \"Default Waterfall\" of the CDCC refers to:",
    options: [
      "The order in which client trades are executed.",
      "The sequence of financial resources used to cover a loss if a clearing member defaults.",
      "The way market data flows from the exchange to the broker.",
      "The process of lowering margin requirements during low volatility.",
    ],
    correct: 1,
    rationale: "The waterfall protects the clearinghouse by using the defaulter's assets first, followed by clearing funds and then mutualized member contributions.",
    type: "hard",
  },
  {
    id: 8,
    text: "A \"Limit Order\" ensures that the trader:",
    options: [
      "Will definitely get a fill.",
      "Will not pay more (or receive less) than a specified price.",
      "Will have their trade executed by the end of the day.",
      "Will not pay any commissions.",
    ],
    correct: 1,
    rationale: "Limit orders offer price protection but no guarantee of execution.",
    type: "concept",
  },
  {
    id: 9,
    text: "A futures contract is \"Marked-to-Market\" at a settlement price of $85.50. If the previous day's settlement was $84.00, what is the cash adjustment for a trader who is Long 5 contracts (Multiplier = $1,000)?",
    options: ["$1,500 Debit", "$7,500 Debit", "$7,500 Credit", "$1,500 Credit"],
    correct: 2,
    rationale: "($85.50 − $84.00) × $1,000 × 5 contracts = $7,500. Since it is a long position and the price rose, it is a credit.",
    type: "numerical",
  },
  {
    id: 10,
    text: "The \"Opening Rotation\" on the Montreal Exchange is a process used to:",
    options: [
      "Allow RRs to choose their seats.",
      "Establish a fair opening price for each option series based on the accumulation of overnight orders.",
      "Calculate the commissions for the day.",
      "Test the electronic servers.",
    ],
    correct: 1,
    rationale: "The rotation ensures an orderly start to the trading session.",
    type: "concept",
  },
  {
    id: 11,
    text: "Which order type is automatically cancelled at the end of the trading session if not filled?",
    options: [
      "GTC (Good-Til-Cancelled).",
      "Day Order.",
      "FOK (Fill-or-Kill).",
      "Stop Order.",
    ],
    correct: 1,
    rationale: "Day orders are only valid for the session in which they are entered.",
    type: "concept",
  },
  {
    id: 12,
    text: "A firm accidentally enters an order for 1,000 contracts instead of 10. According to MX rules, the firm must:",
    options: [
      "Hope no one notices.",
      "Contact the Exchange Control Center (ECC) immediately to request a trade cancellation or adjustment.",
      "Force the client to pay for all 1,000 contracts.",
      "Sell the extra 990 contracts at any price.",
    ],
    correct: 1,
    rationale: "Exchanges have specific Error Trade policies to handle significant clerical mistakes that could disrupt the market.",
    type: "case",
  },
  {
    id: 13,
    text: "\"Position Limits\" are monitored by the exchange and clearinghouse to prevent:",
    options: [
      "Too much profit being made by one trader.",
      "Market manipulation or a single entity from cornering the market.",
      "The RR from working too many hours.",
      "Taxes from being calculated incorrectly.",
    ],
    correct: 1,
    rationale: "Limits ensure no single party has a large enough position to artificially control prices.",
    type: "concept",
  },
  {
    id: 14,
    text: "An \"Exercise Notice\" submitted by a client to their broker is:",
    options: [
      "Revocable until the end of the day.",
      "Irrevocable once it is submitted to the CDCC.",
      "Only valid if the option is at-the-money.",
      "Hand-delivered by the RR.",
    ],
    correct: 1,
    rationale: "For certainty in the clearing process, exercise notices cannot be taken back once processed.",
    type: "concept",
  },
  {
    id: 15,
    text: "An investor is short an option that is assigned. If the strike price is $50, the contract is for 100 shares, and the assignment occurs on a Friday, the investor must deliver the shares by:",
    options: ["Friday (Same day)", "Monday", "Tuesday", "The following Friday"],
    correct: 1,
    rationale: "Under T+1, a Friday exercise results in a Monday settlement (excluding holidays).",
    type: "numerical",
  },
  {
    id: 16,
    text: "Which of the following is a \"Clearing Member\"?",
    options: [
      "Any retail investor.",
      "An individual Registered Representative.",
      "A financial institution or brokerage firm that meets CDCC capital requirements.",
      "The Montreal Exchange itself.",
    ],
    correct: 2,
    rationale: "Only firms with significant capital can be clearing members and interact directly with the CDCC.",
    type: "concept",
  },
  {
    id: 17,
    text: "\"Assignment\" is the process where:",
    options: [
      "An RR is assigned a new client.",
      "The CDCC randomly selects a short option holder to fulfill their obligation after a holder exercises.",
      "The exchange assigns a new ticker symbol to a stock.",
      "A trader is assigned a desk on the trading floor.",
    ],
    correct: 1,
    rationale: "The random selection ensures fairness among all short position holders.",
    type: "concept",
  },
  {
    id: 18,
    text: "\"Trade Matching\" occurs in the MX electronic system (SOLA) based primarily on:",
    options: [
      "The size of the firm.",
      "Price-Time Priority.",
      "The RR's experience.",
      "The alphabetical order of the client's name.",
    ],
    correct: 1,
    rationale: "The best price is filled first; if prices are equal, the order entered first is filled first.",
    type: "hard",
  },
  {
    id: 19,
    text: "Which document summarizes the clearinghouse's role and is mandatory to provide to new clients?",
    options: [
      "The CDCC Information Summary.",
      "The RR's Business Card.",
      "The Monthly Statement.",
      "The Corporate Resolution.",
    ],
    correct: 0,
    rationale: "This disclosure is required to ensure clients understand how their trades are cleared and guaranteed.",
    type: "concept",
  },
  {
    id: 20,
    text: "A trader enters an \"AON\" (All-or-None) order for 50 contracts. There is a bid for 30 contracts and another bid for 20 contracts at their price. Will the order fill?",
    options: [
      "No, because it must be one single buyer for 50.",
      "Yes, because the total available volume matches the \"All\" requirement.",
      "No, because AON orders are illegal in Canada.",
      "Yes, but only if the RR manually approves it.",
    ],
    correct: 1,
    rationale: "AON requires the full quantity to be executed, but it can be filled by multiple counterparties.",
    type: "numerical",
  },
  {
    id: 21,
    text: "\"Intra-day Settlement\" may be called by the CDCC if:",
    options: [
      "The market is very quiet.",
      "There is extreme market volatility and significant price swings.",
      "It is a Friday afternoon.",
      "The RR requests it.",
    ],
    correct: 1,
    rationale: "If prices move drastically, the clearinghouse may require cash payments mid-day to manage rising risk.",
    type: "concept",
  },
  {
    id: 22,
    text: "A client wants to trade during the \"Extended Term\" (after-hours) session on the MX. The RR must inform them that:",
    options: [
      "Liquidity is typically much higher than the day session.",
      "Spreads may be wider and liquidity lower than during regular hours.",
      "There are no after-hours sessions for derivatives.",
      "Commissions are waived during this time.",
    ],
    correct: 1,
    rationale: "Extended sessions often have fewer participants, increasing the cost of trading (wider bid-ask spreads).",
    type: "case",
  },
  {
    id: 23,
    text: "\"Cabinet Trades\" (or $0.01 trades) are used to:",
    options: [
      "Buy expensive furniture for the office.",
      "Allow a trader to close out deep out-of-the-money, worthless positions for a nominal cost to clean up their account.",
      "Cheat the tax system.",
      "Hide trades from the regulator.",
    ],
    correct: 1,
    rationale: "This is a specialized closing transaction for worthless options.",
    type: "concept",
  },
  {
    id: 24,
    text: "Which of the following is NOT a responsibility of the CDCC?",
    options: [
      "Guaranteeing the performance of cleared contracts.",
      "Setting the strike prices and expiration dates for options.",
      "Determining the initial margin requirements.",
      "Recommending specific stocks to retail investors.",
    ],
    correct: 3,
    rationale: "The clearinghouse is a back-office utility, not an investment advisor.",
    type: "concept",
  },
  {
    id: 25,
    text: "A trader is short an index future. The market is at 1,500. At the end of the day, the settlement price is 1,480. If the multiplier is $200, the trader's account will be:",
    options: ["Debited $4,000.", "Credited $4,000.", "Debited $20.", "Credited $20."],
    correct: 1,
    rationale: "Short positions profit when prices fall. (1,500 − 1,480) × $200 = $4,000 credit.",
    type: "numerical",
  },
  {
    id: 26,
    text: "\"Large Open Position Reporting\" (LOPR) is a requirement where:",
    options: [
      "RRs report their personal bank accounts.",
      "Firms must report accounts that exceed certain volume thresholds to regulators.",
      "Clients must post their trades on social media.",
      "The exchange must publish every trade name.",
    ],
    correct: 1,
    rationale: "LOPR helps regulators monitor market concentration.",
    type: "concept",
  },
  {
    id: 27,
    text: "An \"Error Trade\" that is clearly outside the \"No-Cancel Range\" (NCR) is:",
    options: [
      "Automatically cancelled by the SOLA system.",
      "Subject to review and potential cancellation by MX Market Officials.",
      "Irreversible under all circumstances.",
      "Only cancelled if the RR pays a fine.",
    ],
    correct: 1,
    rationale: "The NCR defines the price range where trades are considered final; outside this range, officials can intervene.",
    type: "concept",
  },
  {
    id: 28,
    text: "The \"Give-up\" process in clearing allows:",
    options: [
      "A trader to quit their job.",
      "An institutional client to execute a trade with one broker but clear it through another.",
      "A broker to keep a client's profit.",
      "The exchange to close early.",
    ],
    correct: 1,
    rationale: "This is common for institutions that use multiple execution brokers but centralize their clearing.",
    type: "hard",
  },
  {
    id: 29,
    text: "Settlement of \"Physical Delivery\" futures (like Government of Canada Bonds) involves:",
    options: [
      "A cash transfer of the profit only.",
      "The actual transfer of the underlying bonds from the seller to the buyer.",
      "Buying the bonds at the supermarket.",
      "A guarantee of a 5% return.",
    ],
    correct: 1,
    rationale: "Unlike cash-settled index futures, bond futures require the delivery of a specific asset.",
    type: "concept",
  },
  {
    id: 30,
    text: "The \"Trade Confirmation\" sent to the client must include:",
    options: [
      "The RR's home address.",
      "The price, quantity, commission, and execution venue.",
      "A list of all other clients who made the same trade.",
      "A weather forecast for the settlement date.",
    ],
    correct: 1,
    rationale: "This is the official legal record of the transaction.",
    type: "concept",
  },
  {
    id: 31,
    text: "The \"Open Interest\" of a specific option series is typically updated by the exchange:",
    options: [
      "Every time a trade occurs.",
      "Once a day, after the overnight clearing process is complete.",
      "Every hour on the hour.",
      "Only on expiration Friday.",
    ],
    correct: 1,
    rationale: "Open interest is a snapshot of the total number of outstanding contracts, which is reconciled nightly by the CDCC.",
    type: "concept",
  },
  {
    id: 32,
    text: "A trader enters a \"Stop-Loss\" order to sell 10 contracts at $5.20. The current market is $5.50. The price hits $5.20, then $5.15, then $5.10. At what price will the trader's order most likely be filled?",
    options: [
      "Exactly $5.20.",
      "At $5.15 or the next best available price.",
      "The order will be cancelled.",
      "At the previous day's settlement price.",
    ],
    correct: 1,
    rationale: "Once a stop price is triggered, it becomes a Market Order, meaning it will be filled at the best available price currently in the book.",
    type: "numerical",
  },
  {
    id: 33,
    text: "Which of the following describes a \"FOK\" (Fill-or-Kill) order?",
    options: [
      "It must be filled immediately and entirely, or it is cancelled.",
      "It can be filled in parts throughout the day.",
      "It stays active until the client cancels it.",
      "It is only used for opening new positions.",
    ],
    correct: 0,
    rationale: "FOK orders are used when a trader requires a specific quantity at a specific price immediately, with no partial fills.",
    type: "concept",
  },
  {
    id: 34,
    text: "An institutional client executes a trade through \"Broker A\" but wants the position to be held and cleared at \"Broker B\" (their primary custodian). This process is known as:",
    options: [
      "A trade reversal.",
      "A \"Give-Up\" agreement.",
      "A front-running violation.",
      "A wash trade.",
    ],
    correct: 1,
    rationale: "Give-ups allow large institutions to use multiple execution venues while centralizing their clearing and reporting.",
    type: "case",
  },
  {
    id: 35,
    text: "\"Cash Settlement\" for a derivative contract means that:",
    options: [
      "The buyer must pay for the trade with physical dollar bills.",
      "The difference between the strike and the settlement price is paid in cash, with no physical asset moving.",
      "The RR gets paid in cash on the same day.",
      "The clearinghouse does not guarantee the trade.",
    ],
    correct: 1,
    rationale: "This is common for index products where physical delivery of all underlying stocks is logistically impossible.",
    type: "concept",
  },
  {
    id: 36,
    text: "The \"ECC\" (Exchange Control Center) at the Montreal Exchange is primarily responsible for:",
    options: [
      "Marketing derivatives to the public.",
      "Monitoring the SOLA trading engine and managing trade cancellations/adjustments.",
      "Calculating the margin for retail clients.",
      "Approving new RR licenses.",
    ],
    correct: 1,
    rationale: "The ECC acts as the air traffic control for the electronic marketplace.",
    type: "concept",
  },
  {
    id: 37,
    text: "An investor is short a Government of Canada Bond Future. The Invoice Price is calculated as (Futures Price × Conversion Factor) + Accrued Interest. With values of $110, 0.90, and $1.50 respectively, what is the price per $100 par value?",
    options: ["$99.00", "$100.50", "$111.50", "$101.50"],
    correct: 1,
    rationale: "($110 × 0.90) + $1.50 = $99.00 + $1.50 = $100.50.",
    type: "numerical",
  },
  {
    id: 38,
    text: "\"Trade Cancellation\" by the MX is most likely to occur if:",
    options: [
      "The client changes their mind.",
      "A trade occurred at a price significantly outside the \"No-Cancel Range\" due to a system or clerical error.",
      "The RR's computer crashes.",
      "The stock price goes up after a buy order.",
    ],
    correct: 1,
    rationale: "To maintain market integrity, the exchange can void erroneous trades that occur at non-market prices.",
    type: "concept",
  },
  {
    id: 39,
    text: "Which of the following best describes the \"Assignment\" process for equity options at the CDCC?",
    options: [
      "First-in, first-out (FIFO).",
      "Pro-rata distribution among all members.",
      "A random selection process.",
      "Based on the size of the clearing member.",
    ],
    correct: 2,
    rationale: "Random assignment ensures that no single firm or investor is unfairly targeted for exercise.",
    type: "hard",
  },
  {
    id: 40,
    text: "A \"GTC\" (Good-Til-Cancelled) order on the MX remains active until:",
    options: [
      "The end of the trading day.",
      "It is filled or manually cancelled by the trader.",
      "The option expires.",
      "Both B and C are correct.",
    ],
    correct: 3,
    rationale: "GTC orders persist across sessions but cannot outlive the life of the contract itself.",
    type: "concept",
  },
  {
    id: 41,
    text: "The \"Settlement Price\" for a futures contract is determined:",
    options: [
      "By the first trade of the day.",
      "By a weighted average of trades occurring during the closing period.",
      "By the CEO of the Montreal Exchange.",
      "By the Bank of Canada's prime rate.",
    ],
    correct: 1,
    rationale: "This ensures the price used for marking-to-market is representative of current market value.",
    type: "concept",
  },
  {
    id: 42,
    text: "A Registered Representative notices a trade was executed at $10.50 when the market was $5.50. The RR should immediately:",
    options: [
      "Tell the client they got a great deal.",
      "Contact the firm's trading desk to report a potential \"Error Trade\" to the MX.",
      "Wait until the end of the day to see if the price fixes itself.",
      "Cancel the trade manually from their own terminal.",
    ],
    correct: 1,
    rationale: "Rapid reporting is essential for the exchange to exercise its power to adjust or cancel error trades.",
    type: "case",
  },
  {
    id: 43,
    text: "\"Physical Delivery\" of a commodity future (like Crude Oil) typically occurs at:",
    options: [
      "The client's house.",
      "An exchange-approved delivery point or warehouse (e.g., Cushing, Oklahoma).",
      "The local bank branch.",
      "The Montreal Exchange headquarters.",
    ],
    correct: 1,
    rationale: "Standardized delivery locations are a key feature of physical futures contracts.",
    type: "concept",
  },
  {
    id: 44,
    text: "A client holds 50 long call options. They submit an exercise notice. How many shares of the underlying stock will they be required to purchase?",
    options: ["50", "500", "5,000", "50,000"],
    correct: 2,
    rationale: "50 contracts × 100 shares per contract = 5,000 shares.",
    type: "numerical",
  },
  {
    id: 45,
    text: "\"Novation\" protects the market from which of the following?",
    options: [
      "Systematic price drops.",
      "Counterparty default risk.",
      "High commission rates.",
      "Excessive volatility.",
    ],
    correct: 1,
    rationale: "Because the CDCC becomes the counterparty, the failure of one firm does not cause a chain reaction of defaults.",
    type: "concept",
  },
  {
    id: 46,
    text: "Which order type allows a trader to specify a price \"better than\" the current market price?",
    options: ["Market Order.", "Limit Order.", "Stop-Loss Order.", "Day Order."],
    correct: 1,
    rationale: "Limit orders specify a maximum buy price or minimum sell price.",
    type: "concept",
  },
  {
    id: 47,
    text: "\"Cabinet Trades\" (also known as $0.01 trades) are available for:",
    options: [
      "Any profitable option.",
      "Options that are deep out-of-the-money and have no significant market value.",
      "Buying new office supplies.",
      "Foreign currency futures only.",
    ],
    correct: 1,
    rationale: "These allow investors to close worthless positions for tax or administrative purposes.",
    type: "concept",
  },
  {
    id: 48,
    text: "The \"No-Cancel Range\" (NCR) is:",
    options: [
      "A physical area on the trading floor.",
      "The price range within which the exchange will generally not cancel a trade, even if it was an error.",
      "The maximum number of contracts an RR can trade.",
      "A time period at the end of the day when orders cannot be cancelled.",
    ],
    correct: 1,
    rationale: "The NCR provides market certainty; if an error occurs but the price is close to the market, it usually stands.",
    type: "hard",
  },
  {
    id: 49,
    text: "An investor is long 10 S&P/TSX 60 Index Futures (multiplier $200). The Mark-to-Market settlement results in a $5,000 credit to their account. By how many points did the index rise?",
    options: ["2.5 points", "5.0 points", "10.0 points", "25.0 points"],
    correct: 0,
    rationale: "Points = Credit ÷ (Multiplier × Contracts) = $5,000 ÷ ($200 × 10) = 2.5 points.",
    type: "numerical",
  },
  {
    id: 50,
    text: "\"T+1 Settlement\" for Canadian derivatives means that the transaction is finalized:",
    options: [
      "On the same day.",
      "On the next business day following the trade.",
      "Two business days after the trade.",
      "Only when the option expires.",
    ],
    correct: 1,
    rationale: "Canada moved to T+1 to reduce risk and align with other major North American markets.",
    type: "concept",
  },
  {
    id: 51,
    text: "Which of the following is responsible for maintaining an orderly market and providing continuous bids and asks?",
    options: [
      "The retail investor.",
      "The Market Maker.",
      "The Compliance Officer.",
      "The CDCC.",
    ],
    correct: 1,
    rationale: "Market makers are obligated to provide liquidity in exchange for certain trading advantages.",
    type: "concept",
  },
  {
    id: 52,
    text: "A client wants to execute a trade but is worried about a sudden price spike. They should use which order type?",
    options: ["Market Order.", "Limit Order.", "Stop Order.", "GTC Order."],
    correct: 1,
    rationale: "Limit orders provide price certainty, ensuring the client doesn't pay more than intended.",
    type: "case",
  },
  {
    id: 53,
    text: "\"Exercise by Exception\" means that:",
    options: [
      "An option is only exercised if the client calls the broker.",
      "The CDCC will automatically exercise any option that is in-the-money by a certain threshold at expiration.",
      "Exercising an option is illegal in Canada.",
      "Only institutional investors can exercise options.",
    ],
    correct: 1,
    rationale: "This protects investors from losing the value of ITM options if they forget to submit a manual notice.",
    type: "concept",
  },
  {
    id: 54,
    text: "The \"Closing Rotation\" is used to:",
    options: [
      "Choose who buys lunch.",
      "Determine the final settlement price for the day in an orderly fashion.",
      "Cancel all unfilled orders.",
      "Open the market for the next day.",
    ],
    correct: 1,
    rationale: "Orderly closing procedures prevent artificial price spikes at the end of the day.",
    type: "concept",
  },
  {
    id: 55,
    text: "A firm's \"Clearing Fund\" contribution is $500,000. The CDCC total clearing fund is $100 million. If a default occurs and the fund is used, the firm's share of the loss is generally based on its:",
    options: [
      "Age.",
      "Pro-rata share of the total clearing fund (0.5% in this case).",
      "Total number of employees.",
      "The CEO's salary.",
    ],
    correct: 1,
    rationale: "Losses are mutualized based on each member's participation in the clearing system.",
    type: "numerical",
  },
  {
    id: 56,
    text: "\"Novation\" results in the creation of:",
    options: ["One contract.", "Two separate and independent contracts.", "A tax liability.", "A new ticker symbol."],
    correct: 1,
    rationale: "The clearinghouse becomes the buyer to the seller and the seller to the buyer.",
    type: "concept",
  },
  {
    id: 57,
    text: "An \"IOC\" (Immediate-or-Cancel) order differs from a \"FOK\" (Fill-or-Kill) order because:",
    options: [
      "IOC allows for partial fills, while FOK does not.",
      "FOK allows for partial fills, while IOC does not.",
      "IOC stays active for 24 hours.",
      "FOK is only for futures.",
    ],
    correct: 0,
    rationale: "Both require immediate action, but IOC will take whatever quantity is available and cancel the rest.",
    type: "hard",
  },
  {
    id: 58,
    text: "\"Assignment\" notices are typically delivered to the short position holder:",
    options: [
      "On the same day the holder exercises.",
      "The morning of the next business day.",
      "Only once a month.",
      "After the stock has already been delivered.",
    ],
    correct: 1,
    rationale: "The CDCC processes exercises overnight and notifies clearing members the following morning.",
    type: "concept",
  },
  {
    id: 59,
    text: "A trader buys 20 Crude Oil Futures at $75.00. They enter a Stop-Loss at $72.00. The market drops to $71.50 in a single trade. What is the trader's approximate loss per barrel?",
    options: ["$3.00", "$3.50", "$72.00", "$0.50"],
    correct: 1,
    rationale: "The stop was triggered at $72, but as a market order, it fills at $71.50. $75.00 − $71.50 = $3.50.",
    type: "numerical",
  },
  {
    id: 60,
    text: "The \"Primary Counterparty\" to all cleared trades on the Montreal Exchange is:",
    options: [
      "The other trader who entered the order.",
      "The Canadian Derivatives Clearing Corporation (CDCC).",
      "The Bank of Canada.",
      "CIRO.",
    ],
    correct: 1,
    rationale: "Through novation, the CDCC guarantees every trade.",
    type: "concept",
  },
  {
    id: 61,
    text: "A \"Market-on-Close\" (MOC) order is designed to be executed:",
    options: [
      "At the exact price of the last trade of the day.",
      "As close to the closing price as possible during the final minutes of trading.",
      "Only if the market is moving higher.",
      "At the opening price of the following day.",
    ],
    correct: 1,
    rationale: "MOC orders are used by managers who want to match the daily settlement price for index tracking or accounting.",
    type: "concept",
  },
  {
    id: 62,
    text: "An investor holds a Long Put that is in-the-money by $2.50 at expiration. The \"Exercise by Exception\" threshold is $0.01, and the investor does nothing. What happens?",
    options: [
      "The option expires worthless.",
      "The CDCC automatically exercises the put, and the investor is credited the intrinsic value.",
      "The investor is fined by the exchange.",
      "The broker keeps the profit.",
    ],
    correct: 1,
    rationale: "Options exceeding the threshold are automatically exercised to protect the investor's equity.",
    type: "numerical",
  },
  {
    id: 63,
    text: "The \"Intra-day Variation Margin\" call is typically issued when:",
    options: [
      "The market is closed for a holiday.",
      "Market volatility causes a clearing member's capital to drop below a specific safety limit mid-session.",
      "A client wants to withdraw cash.",
      "An RR's license is being renewed.",
    ],
    correct: 1,
    rationale: "This is an emergency measure to ensure that losses are collateralized immediately during extreme swings.",
    type: "concept",
  },
  {
    id: 64,
    text: "A clearing member firm goes bankrupt. According to the \"Default Waterfall,\" after using the defaulting firm's own margin and clearing fund contribution, the CDCC will next use:",
    options: [
      "The funds of the retail clients.",
      "The CDCC's own corporate capital (the \"Skin in the Game\").",
      "The clearing funds of all other non-defaulting members.",
      "A loan from the International Monetary Fund.",
    ],
    correct: 1,
    rationale: "The CDCC uses its own capital before mutualizing the loss among other members to show commitment to the system's safety.",
    type: "case",
  },
  {
    id: 65,
    text: "Which of the following is a \"Non-Standard\" settlement process?",
    options: [
      "T+1 share delivery for equity options.",
      "Cash settlement for index options.",
      "Physical Delivery of oil via a pipeline at a specified hub.",
      "Paying the RR in cryptocurrency.",
    ],
    correct: 3,
    rationale: "All exchange-cleared trades must settle in recognized fiat currency (CAD or USD).",
    type: "concept",
  },
  {
    id: 66,
    text: "A trader enters a \"Stop-Limit\" order: Stop at $12.00, Limit at $12.10. The current market is $11.50. If the price hits $12.05, the order:",
    options: [
      "Becomes a market order and fills at $12.05.",
      "Is triggered and sits as a limit order at $12.10.",
      "Is cancelled immediately.",
      "Is only filled if the price stays at $12.05 for 10 minutes.",
    ],
    correct: 1,
    rationale: "The stop activates the order, and the limit ensures the trader doesn't pay more than $12.10.",
    type: "numerical",
  },
  {
    id: 67,
    text: "\"Trade Finality\" in the clearing process is reached when:",
    options: [
      "The client receives an email.",
      "The trade is accepted and registered by the CDCC.",
      "The money is withdrawn from the client's bank.",
      "The option expires.",
    ],
    correct: 1,
    rationale: "Registration by the clearinghouse completes the process of novation.",
    type: "concept",
  },
  {
    id: 68,
    text: "\"Automatic Assignment\" for short option holders is performed by the CDCC using:",
    options: [
      "A first-in, first-out (FIFO) basis.",
      "A wheel-based random selection algorithm.",
      "The largest accounts first.",
      "The smallest accounts first.",
    ],
    correct: 1,
    rationale: "Random selection is the regulatory standard to prevent any bias or manipulation in the assignment process.",
    type: "hard",
  },
  {
    id: 69,
    text: "A clearing member has an Initial Margin requirement of $10,000,000. They deposit $12,000,000 in T-Bills and the \"Haircut\" for T-Bills is 2%. What is their \"Excess Margin\"?",
    options: ["$1,760,000", "$2,000,000", "$2,240,000", "$0"],
    correct: 0,
    rationale: "Value of collateral = $12,000,000 × 0.98 = $11,760,000. Excess = $11,760,000 − $10,000,000 = $1,760,000.",
    type: "numerical",
  },
  {
    id: 70,
    text: "The \"Early Exercise\" of an American-style option is most likely to occur:",
    options: [
      "When the option has zero time value and is deep in-the-money.",
      "Right before a large dividend is paid on the underlying stock.",
      "When volatility is at an all-time high.",
      "Both A and B are correct.",
    ],
    correct: 3,
    rationale: "Investors exercise early to capture a dividend or when the cost of holding the option (interest/time) exceeds the benefit of keeping it open.",
    type: "hard",
  },
  {
    id: 71,
    text: "\"Novation\" ensures that if \"Trader A\" goes bust, \"Trader B\" (the counterparty):",
    options: [
      "Also goes bust.",
      "Is still paid by the CDCC.",
      "Must sue Trader A in court.",
      "Forfeits their profit.",
    ],
    correct: 1,
    rationale: "The CDCC's guarantee is the primary reason the market functions without credit concerns.",
    type: "concept",
  },
  {
    id: 72,
    text: "A client wants to \"Sell to Close\" a position, but there are no bids. The RR mentions a \"Cabinet Trade.\" This means the client will receive:",
    options: [
      "The full market value.",
      "A nominal price (e.g., $1.00 per contract) just to exit the obligation and realize the tax loss.",
      "A refund from the exchange.",
      "Extra shares of stock.",
    ],
    correct: 1,
    rationale: "Cabinet trades are a service for closing out worthless positions.",
    type: "case",
  },
  {
    id: 73,
    text: "\"Position Aggregation\" rules mean that for LOPR purposes, the exchange looks at:",
    options: [
      "Only individual accounts.",
      "All accounts under the same \"beneficial ownership\" or acting in concert.",
      "Only the firm's house account.",
      "Accounts in different countries.",
    ],
    correct: 1,
    rationale: "To prevent manipulation, regulators group related accounts together when checking limits.",
    type: "concept",
  },
  {
    id: 74,
    text: "An investor is short a bond future. The \"Conversion Factor\" for the bond they are delivering is 0.82. If the futures price is $105, the adjusted price paid (before interest) is:",
    options: ["$105.00", "$82.00", "$86.10", "$128.05"],
    correct: 2,
    rationale: "105 × 0.82 = $86.10.",
    type: "numerical",
  },
  {
    id: 75,
    text: "\"Settlement Risk\" is highest in which of the following?",
    options: [
      "Exchange-traded futures.",
      "Exchange-traded options.",
      "Over-the-counter (OTC) forwards.",
      "Government of Canada T-Bills.",
    ],
    correct: 2,
    rationale: "Without a clearinghouse, the risk that one party fails to deliver on the settlement date is significant.",
    type: "concept",
  },
  {
    id: 76,
    text: "Which of the following is an \"Approved Participant\" on the Montreal Exchange?",
    options: [
      "Any Canadian citizen.",
      "A brokerage firm that has been granted access to the electronic trading system.",
      "Only the Big 5 banks.",
      "An individual trader with more than $1,000,000.",
    ],
    correct: 1,
    rationale: "Participants must meet specific regulatory and technical standards.",
    type: "concept",
  },
  {
    id: 77,
    text: "An index option expires. The strike is 1,250 and the final settlement value of the index is 1,262. For 5 long calls, the cash settlement amount is:",
    options: ["$12.00", "$6,000.00", "$12,000.00", "$60,000.00"],
    correct: 2,
    rationale: "(1,262 − 1,250) × $200 (multiplier) × 5 contracts = 12 × 1,000 = $12,000.",
    type: "numerical",
  },
  {
    id: 78,
    text: "\"Exercise by Exception\" usually applies to options that are in-the-money by at least:",
    options: [
      "$0.01 per share.",
      "$1.00 per share.",
      "5% of the strike price.",
      "The amount of the commission.",
    ],
    correct: 0,
    rationale: "The threshold is purposefully low to ensure any positive value is captured for the client.",
    type: "concept",
  },
  {
    id: 79,
    text: "The \"SOLA\" system is:",
    options: [
      "A type of margin calculation.",
      "The electronic trading platform used by the Montreal Exchange.",
      "A tax form for derivatives.",
      "A specialized clearinghouse for gold.",
    ],
    correct: 1,
    rationale: "SOLA is the technical infrastructure where orders are matched.",
    type: "concept",
  },
  {
    id: 80,
    text: "A clearing firm is unable to meet a $50 million variation margin call. The CDCC declares a default. The very FIRST thing the CDCC does is:",
    options: [
      "Call the police.",
      "Close out or transfer all of the defaulting firm's open positions to other members.",
      "File for bankruptcy.",
      "Use the other members' clearing funds.",
    ],
    correct: 1,
    rationale: "Immediate risk neutralisation is the priority in a default scenario.",
    type: "case",
  },
  {
    id: 81,
    text: "\"Mark-to-Market\" gains are added to the client's account as:",
    options: [
      "\"Unrealized\" equity.",
      "\"Realized\" cash that can be withdrawn (if above initial margin).",
      "A credit toward next year's commissions.",
      "Extra contracts.",
    ],
    correct: 1,
    rationale: "This is a key difference between futures and stocks.",
    type: "concept",
  },
  {
    id: 82,
    text: "A \"Limit Order\" to buy 10 contracts is entered at $3.40. The market is $3.35 − $3.45. What happens?",
    options: [
      "The order is filled at $3.45.",
      "The order is filled at $3.35 (or the best available price up to $3.40).",
      "The order is cancelled because the price isn't exactly $3.40.",
      "The trader must pay $3.40.",
    ],
    correct: 1,
    rationale: "A buy limit will fill at the specified price or better.",
    type: "numerical",
  },
  {
    id: 83,
    text: "\"Short Assignment\" risk is greatest when:",
    options: [
      "The market is closed.",
      "The option is deep out-of-the-money.",
      "The underlying stock is about to go ex-dividend.",
      "The RR is on vacation.",
    ],
    correct: 2,
    rationale: "Call holders exercise early to own the stock and receive the dividend.",
    type: "concept",
  },
  {
    id: 84,
    text: "The \"Clearing Fund\" of the CDCC acts as:",
    options: [
      "A marketing budget.",
      "A mutualized insurance pool to cover extreme losses beyond a defaulter's margin.",
      "A way to pay for exchange parties.",
      "A source of interest for the clearinghouse.",
    ],
    correct: 1,
    rationale: "It is the core of the safety net for the Canadian derivatives market.",
    type: "concept",
  },
  {
    id: 85,
    text: "A trader is long 10 futures. The price falls by $2.00 per unit. If the multiplier is 1,000, the \"Variation Margin\" payment required is:",
    options: ["$2,000", "$10,000", "$20,000", "$0"],
    correct: 2,
    rationale: "$2.00 × 1,000 × 10 = $20,000.",
    type: "numerical",
  },
  {
    id: 86,
    text: "\"Pro-rata\" allocation of assignment is:",
    options: [
      "Used by the CDCC.",
      "Sometimes used by brokerage firms when deciding which of their own clients will be assigned after the firm receives an assignment from the CDCC.",
      "Required by the CRA.",
      "Illegal in Canada.",
    ],
    correct: 1,
    rationale: "While the CDCC is random, firms can choose between random or pro-rata for their own internal client allocation.",
    type: "concept",
  },
  {
    id: 87,
    text: "\"Physical Settlement\" for a bond future involves:",
    options: [
      "Printing out a paper bond.",
      "The transfer of the bond in the CDS (Canadian Depository for Securities) system.",
      "Paying cash.",
      "Mailing a check.",
    ],
    correct: 1,
    rationale: "Modern physical delivery is done electronically through central depositories.",
    type: "concept",
  },
  {
    id: 88,
    text: "An \"Opening Rotation\" order that is not filled during the opening becomes:",
    options: [
      "A cancelled order.",
      "A regular \"Day Order\" at its limit price.",
      "An \"Extended Term\" order.",
      "A market order.",
    ],
    correct: 1,
    rationale: "Unless specified otherwise, orders persist into the regular session.",
    type: "hard",
  },
  {
    id: 89,
    text: "The \"Exercise Settlement Value\" (ESV) for the S&P/TSX 60 Index is based on:",
    options: [
      "The closing price on Thursday.",
      "The opening prices of the 60 stocks on the third Friday of the month.",
      "The average price over the whole month.",
      "The price at midnight.",
    ],
    correct: 1,
    rationale: "This prevents a single large trade at the close from manipulating the final settlement.",
    type: "concept",
  },
  {
    id: 90,
    text: "\"Novation\" occurs at the point of:",
    options: [
      "Order entry.",
      "Trade execution.",
      "Trade registration/acceptance by the clearinghouse.",
      "Settlement.",
    ],
    correct: 2,
    rationale: "The CDCC must accept the trade before it legally becomes the counterparty.",
    type: "concept",
  },
  {
    id: 91,
    text: "If an investor is \"Short\" an option and the holder \"Exercises,\" the process of choosing which writer must fulfill the obligation is called:",
    options: ["Allotment.", "Assignment.", "Novation.", "Selection."],
    correct: 1,
    rationale: "Assignment is the formal notice from the clearinghouse to the seller that they must deliver the underlying asset or cash.",
    type: "concept",
  },
  {
    id: 92,
    text: "A client holds 5 Silver futures (Multiplier = 5,000 oz). The Delivery Notice is issued when Silver is at $24.00. What is the total dollar value of the silver they must pay for?",
    options: ["$120,000", "$240,000", "$600,000", "$1,200,000"],
    correct: 2,
    rationale: "5 contracts × 5,000 oz × $24.00 = $600,000.",
    type: "numerical",
  },
  {
    id: 93,
    text: "The \"Trade Match\" process on SOLA is based on \"Price-Time Priority.\" This means:",
    options: [
      "The biggest order always fills first.",
      "The best price is filled first; if prices are equal, the order that entered the system first is filled first.",
      "All orders are filled at the same average price.",
      "Only institutional orders are filled during the first hour.",
    ],
    correct: 1,
    rationale: "This ensures market fairness and encourages traders to provide competitive prices early.",
    type: "concept",
  },
  {
    id: 94,
    text: "A client wants to exercise an option on the last day of trading. The RR fails to submit the notice by the firm's internal cutoff. If the option is in-the-money by $0.05, the client is protected by:",
    options: [
      "The RR's insurance.",
      "\"Exercise by Exception\" (Automatic Exercise) at the CDCC level.",
      "A refund from the Montreal Exchange.",
      "Nothing; the value is lost.",
    ],
    correct: 1,
    rationale: "CDCC rules automatically exercise options that meet a minimum threshold ($0.01 for most) to prevent losses due to administrative errors.",
    type: "case",
  },
  {
    id: 95,
    text: "\"Variation Margin\" is distinct from \"Initial Margin\" because it:",
    options: [
      "Is paid only by the buyer.",
      "Represents the actual daily profit or loss moved between accounts in cash.",
      "Is a one-time fee.",
      "Cannot be withdrawn.",
    ],
    correct: 1,
    rationale: "Variation margin is the liquid part of futures trading that keeps the contract value current.",
    type: "concept",
  },
  {
    id: 96,
    text: "Which entity has the authority to \"Halt Trading\" in a specific derivative series if the underlying stock is suspended?",
    options: [
      "The Bank of Canada.",
      "The Montreal Exchange (MX).",
      "The individual Registered Representative.",
      "The Canadian Depository for Securities (CDS).",
    ],
    correct: 1,
    rationale: "The exchange must maintain an orderly market; if the underlying asset isn't trading, the derivative shouldn't either.",
    type: "concept",
  },
  {
    id: 97,
    text: "An investor is Long 10 Bond Futures. A Tick is 0.01 and is worth $10. If the market moves from 112.45 to 112.50, what is the daily Mark-to-Market credit to the account?",
    options: ["$50", "$500", "$5,000", "$50,000"],
    correct: 1,
    rationale: "The move is 5 ticks. $10/tick × 5 ticks × 10 contracts = $500.",
    type: "numerical",
  },
  {
    id: 98,
    text: "\"Delivery Fails\" in the futures market are considered:",
    options: [
      "A minor inconvenience.",
      "A serious regulatory violation that may result in fines and disciplinary action for the clearing member.",
      "A standard way to cancel a trade.",
      "The responsibility of the retail client only.",
    ],
    correct: 1,
    rationale: "The integrity of the delivery process is vital to the market's function as a price discovery tool.",
    type: "concept",
  },
  {
    id: 99,
    text: "A \"Give-Up\" agreement is a three-way contract between:",
    options: [
      "The Client, the Executing Broker, and the Clearing Broker.",
      "The Client, the RR, and the Manager.",
      "The MX, the CDCC, and CIRO.",
      "The Bank, the Government, and the Exchange.",
    ],
    correct: 0,
    rationale: "It allows the separation of trade execution and trade custody/clearing.",
    type: "concept",
  },
  {
    id: 100,
    text: "\"Novation\" occurs at which stage of the trade life cycle?",
    options: [
      "Order entry.",
      "Trade execution.",
      "Trade registration/acceptance by the CDCC.",
      "Monthly statement generation.",
    ],
    correct: 2,
    rationale: "Only when the clearinghouse accepts the trade does it legally step in as the counterparty.",
    type: "concept",
  },
];

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const TYPE_LABELS = { concept: "Conceptual", numerical: "Numerical", case: "Case Study", hard: "Hard" };
const TYPE_COLORS = {
  concept: { bg: "#dbeafe", text: "#1e40af" },
  numerical: { bg: "#dcfce7", text: "#166534" },
  case: { bg: "#fef3c7", text: "#92400e" },
  hard: { bg: "#fee2e2", text: "#991b1b" },
};
const QUIZ_DURATION = 75 * 60;
const fmt2 = (n) => String(n).padStart(2, "0");
const fmtTime = (s) => `${fmt2(Math.floor(s / 60))}:${fmt2(s % 60)}`;

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────
const VittaLogo = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <div style={{
      width: 36, height: 36, borderRadius: "50%",
      background: "linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 2px 8px rgba(30,58,95,0.25)",
    }}>
      <span style={{ color: "#d4a017", fontFamily: "Georgia,serif", fontWeight: "bold", fontSize: 15 }}>V</span>
    </div>
    <div>
      <div style={{ fontFamily: "Georgia,serif", fontWeight: "bold", fontSize: 13, color: "#1e3a5f", letterSpacing: 1.5, textTransform: "uppercase" }}>Vitta Finance Academy</div>
      <div style={{ fontSize: 10, color: "#64748b", letterSpacing: 0.5 }}>CIRO Derivatives Exam Prep</div>
    </div>
  </div>
);

const TypeBadge = ({ type }) => {
  const c = TYPE_COLORS[type] || TYPE_COLORS.concept;
  return (
    <span style={{ background: c.bg, color: c.text, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>
      {TYPE_LABELS[type] || type}
    </span>
  );
};

const ProgressBar = ({ current, total, answered }) => (
  <div style={{ padding: "10px 24px 0", background: "#fff" }}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
      <span style={{ fontSize: 11, color: "#64748b" }}>Q {current} of {total}</span>
      <span style={{ fontSize: 11, color: "#64748b" }}>{answered} answered</span>
    </div>
    <div style={{ height: 4, background: "#e2e8f0", borderRadius: 2, overflow: "hidden" }}>
      <div style={{
        height: "100%", borderRadius: 2,
        background: "linear-gradient(90deg, #1e3a5f, #2d5986)",
        width: `${(answered / total) * 100}%`, transition: "width 0.3s ease",
      }} />
    </div>
  </div>
);

const QuestionCard = ({ question, selectedAnswer, onSelect, showResult }) => {
  const letters = ["A", "B", "C", "D"];
  return (
    <div style={{ padding: "20px 24px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ background: "#1e3a5f", color: "#d4a017", fontFamily: "Georgia,serif", fontWeight: "bold", fontSize: 13, borderRadius: 8, padding: "4px 12px", letterSpacing: 0.5 }}>
          Q{question.id}
        </div>
        <TypeBadge type={question.type} />
      </div>
      <p style={{ fontFamily: "Georgia,serif", fontSize: 15.5, color: "#0f172a", lineHeight: 1.7, margin: "0 0 18px" }}>
        {question.text}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {question.options.map((opt, i) => {
          const isSelected = selectedAnswer === i;
          const isCorrect = i === question.correct;
          let bg = "#f8fafc", border = "1px solid #e2e8f0", textColor = "#1e293b", weight = "normal";
          if (showResult) {
            if (isCorrect) { bg = "#dcfce7"; border = "1.5px solid #166534"; textColor = "#166534"; weight = "600"; }
            else if (isSelected) { bg = "#fee2e2"; border = "1.5px solid #991b1b"; textColor = "#991b1b"; weight = "600"; }
          } else if (isSelected) { bg = "#dbeafe"; border = "1.5px solid #1e40af"; textColor = "#1e40af"; weight = "600"; }
          return (
            <button key={i} onClick={() => !showResult && onSelect(i)} style={{ background: bg, border, borderRadius: 10, padding: "12px 16px", textAlign: "left", cursor: showResult ? "default" : "pointer", transition: "all 0.15s", width: "100%", display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span style={{ minWidth: 24, height: 24, borderRadius: 6, background: showResult ? isCorrect ? "#166534" : isSelected ? "#991b1b" : "#e2e8f0" : isSelected ? "#1e40af" : "#e2e8f0", color: (isSelected || (showResult && isCorrect)) ? "#fff" : "#64748b", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: 12, flexShrink: 0 }}>
                {showResult && isCorrect ? "✓" : showResult && isSelected && !isCorrect ? "✗" : letters[i]}
              </span>
              <span style={{ fontSize: 14, color: textColor, fontWeight: weight, lineHeight: 1.5 }}>{opt}</span>
            </button>
          );
        })}
      </div>
      {showResult && (
        <div style={{ marginTop: 16, padding: "14px 16px", background: "#f0fdf4", borderRadius: 10, borderLeft: "4px solid #166534" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#166534", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Rationale</div>
          <p style={{ margin: 0, fontSize: 13.5, color: "#166534", lineHeight: 1.6 }}>{question.rationale}</p>
        </div>
      )}
    </div>
  );
};

const DotNavigator = ({ total, current, answers, onGo }) => (
  <div style={{ padding: "14px 20px", overflowX: "auto", borderTop: "1px solid #e2e8f0" }}>
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: "center" }}>
      {Array.from({ length: total }, (_, i) => {
        const answered = answers[i] !== undefined, isCurrent = i === current;
        return (
          <button key={i} onClick={() => onGo(i)} style={{ width: isCurrent ? 28 : 22, height: isCurrent ? 28 : 22, borderRadius: 6, background: isCurrent ? "#1e3a5f" : answered ? "#d4a017" : "#e2e8f0", color: (isCurrent || answered) ? "#fff" : "#94a3b8", border: "none", cursor: "pointer", fontSize: 9, fontWeight: "bold", transition: "all 0.15s", flexShrink: 0 }}>
            {i + 1}
          </button>
        );
      })}
    </div>
    <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 8 }}>
      {[["#1e3a5f", "Current"], ["#d4a017", "Answered"], ["#e2e8f0", "Unanswered"]].map(([color, label]) => (
        <span key={label} style={{ fontSize: 10, color: "#64748b", display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 10, height: 10, background: color, borderRadius: 3, display: "inline-block" }} /> {label}
        </span>
      ))}
    </div>
  </div>
);

const NavButtons = ({ current, total, onPrev, onNext, extra }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "16px 24px 20px", borderTop: "1px solid #f1f5f9", marginTop: 16 }}>
    <button onClick={onPrev} disabled={current === 0} style={{ background: current === 0 ? "#f1f5f9" : "#f8fafc", color: current === 0 ? "#94a3b8" : "#1e3a5f", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: current === 0 ? "default" : "pointer" }}>← Prev</button>
    <div style={{ display: "flex", gap: 8 }}>{extra}</div>
    <button onClick={onNext} disabled={current === total - 1} style={{ background: current === total - 1 ? "#f1f5f9" : "linear-gradient(135deg,#1e3a5f,#2d5986)", color: current === total - 1 ? "#94a3b8" : "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: current === total - 1 ? "default" : "pointer", boxShadow: current === total - 1 ? "none" : "0 2px 8px rgba(30,58,95,0.2)" }}>Next →</button>
  </div>
);

// ─── HOME SCREEN ──────────────────────────────────────────────────────────────
const HomeScreen = ({ onMode }) => (
  <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
    <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 4px 32px rgba(30,58,95,0.10)", padding: "48px 40px", maxWidth: 520, width: "100%", textAlign: "center", border: "1px solid #e2e8f0" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "linear-gradient(135deg,#1e3a5f,#2d5986)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", boxShadow: "0 6px 20px rgba(30,58,95,0.25)" }}>
          <span style={{ color: "#d4a017", fontFamily: "Georgia,serif", fontWeight: "bold", fontSize: 30 }}>V</span>
        </div>
        <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "#d4a017", fontWeight: 600, marginBottom: 6 }}>Vitta Finance Academy</div>
        <h1 style={{ fontFamily: "Georgia,serif", fontSize: 26, fontWeight: "bold", color: "#0f172a", margin: 0, lineHeight: 1.3 }}>CIRO Derivatives Exam</h1>
        <div style={{ display: "inline-block", marginTop: 10, padding: "5px 18px", background: "#1e3a5f", color: "#fff", borderRadius: 20, fontSize: 13, fontWeight: 600, letterSpacing: 0.5 }}>
          Element 5: Trading, Clearing & Settlement
        </div>
      </div>
      <p style={{ color: "#475569", fontSize: 14, lineHeight: 1.7, margin: "20px 0 28px" }}>
        100 questions covering novation, order types, CDCC operations, mark-to-market, settlement mechanics, and the default waterfall.
      </p>
      <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
        {[["📋", "100", "Questions"], ["📚", "8", "Topics"], ["⏱", "75 min", "Quiz Time"]].map(([icon, value, label]) => (
          <div key={label} style={{ background: "#f1f5f9", borderRadius: 12, padding: "12px 18px", textAlign: "center", minWidth: 90 }}>
            <div style={{ fontSize: 18 }}>{icon}</div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: 20, fontWeight: "bold", color: "#1e3a5f" }}>{value}</div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <button onClick={() => onMode("practice")} style={{ background: "linear-gradient(135deg,#1e3a5f,#2d5986)", color: "#fff", border: "none", borderRadius: 12, padding: "16px 24px", fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(30,58,95,0.30)", fontFamily: "Georgia,serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
          <span>📖</span> Practice Mode
        </button>
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: -4, marginBottom: 4 }}>Instant feedback · Rationale explanations · Missed question review</div>
        <button onClick={() => onMode("quiz")} style={{ background: "#fff", color: "#1e3a5f", border: "2px solid #1e3a5f", borderRadius: 12, padding: "16px 24px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "Georgia,serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
          <span>⏱</span> Timed Quiz Mode
        </button>
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: -4 }}>75-minute exam simulation · Full diagnostics &amp; score breakdown</div>
      </div>
    </div>
    <div style={{ marginTop: 20, display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
      {Object.keys(TYPE_LABELS).map(k => <TypeBadge key={k} type={k} />)}
    </div>
  </div>
);

// ─── PRACTICE MODE ────────────────────────────────────────────────────────────
const PracticeResults = ({ score, total, missed, onHome, onReview, onRestart }) => {
  const pct = total > 0 ? Math.round((score / total) * 100) : 0;
  const pass = pct >= 60;
  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", padding: "24px 16px" }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 4px 24px rgba(30,58,95,0.10)", border: "1px solid #e2e8f0", padding: "32px 28px", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>{pass ? "🎯" : "📚"}</div>
          <h2 style={{ fontFamily: "Georgia,serif", color: "#0f172a", margin: "0 0 4px", fontSize: 22 }}>Practice Results</h2>
          <div style={{ fontSize: 48, fontFamily: "Georgia,serif", fontWeight: "bold", color: pass ? "#166534" : "#991b1b", margin: "12px 0" }}>{pct}%</div>
          <div style={{ fontSize: 14, color: "#64748b", marginBottom: 20 }}>{score} correct out of {total} attempted</div>
          {missed.length > 0 && (
            <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "12px 16px", marginBottom: 20, textAlign: "left" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>Missed {missed.length} question{missed.length !== 1 ? "s" : ""}:</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {missed.map(i => <span key={i} style={{ background: "#fee2e2", color: "#991b1b", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10 }}>Q{i + 1}</span>)}
              </div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {missed.length > 0 && <button onClick={onReview} style={{ background: "linear-gradient(135deg,#991b1b,#b91c1c)", color: "#fff", border: "none", borderRadius: 10, padding: "13px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>📝 Review Missed Questions ({missed.length})</button>}
            <button onClick={onRestart} style={{ background: "linear-gradient(135deg,#1e3a5f,#2d5986)", color: "#fff", border: "none", borderRadius: 10, padding: "13px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>🔄 Restart Practice</button>
            <button onClick={onHome} style={{ background: "#f1f5f9", color: "#1e3a5f", border: "1px solid #e2e8f0", borderRadius: 10, padding: "13px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>← Back to Home</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PracticeMode = ({ onHome }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [subMode, setSubMode] = useState("practice");

  const q = QUESTIONS[current];
  const totalAnswered = Object.keys(answers).length;
  const isRevealed = !!revealed[current];
  const score = Object.entries(answers).filter(([i, a]) => a === QUESTIONS[parseInt(i)].correct).length;
  const missedIdx = Object.entries(answers).filter(([i, a]) => a !== QUESTIONS[parseInt(i)].correct).map(([i]) => parseInt(i));

  const handleSelect = (i) => {
    if (isRevealed) return;
    setAnswers(p => ({ ...p, [current]: i }));
    setRevealed(p => ({ ...p, [current]: true }));
  };

  if (subMode === "results") return <PracticeResults score={score} total={totalAnswered} missed={missedIdx} onHome={onHome} onReview={() => { setSubMode("missed"); setCurrent(missedIdx[0] || 0); }} onRestart={() => { setAnswers({}); setRevealed({}); setCurrent(0); setSubMode("practice"); }} />;

  const displayQs = subMode === "missed" ? missedIdx.map(i => QUESTIONS[i]) : QUESTIONS;
  const displayQ = displayQs[current] || QUESTIONS[0];

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, position: "sticky", top: 0, zIndex: 10 }}>
        <VittaLogo />
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ background: "#dbeafe", color: "#1e40af", padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>📖 Practice Mode</span>
          <span style={{ fontSize: 12, color: "#64748b" }}>{score}/{totalAnswered} correct</span>
          {totalAnswered > 0 && <button onClick={() => setSubMode("results")} style={{ background: "#1e3a5f", color: "#d4a017", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>See Results</button>}
          <button onClick={onHome} style={{ background: "none", border: "1px solid #e2e8f0", color: "#64748b", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>← Home</button>
        </div>
      </div>
      <ProgressBar current={current + 1} total={QUESTIONS.length} answered={totalAnswered} />
      <div style={{ flex: 1, maxWidth: 760, margin: "0 auto", width: "100%", paddingBottom: 20 }}>
        <div style={{ background: "#fff", margin: "16px 16px 0", borderRadius: 14, boxShadow: "0 2px 12px rgba(30,58,95,0.08)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <QuestionCard question={displayQ} selectedAnswer={answers[subMode === "missed" ? missedIdx[current] : current]} onSelect={handleSelect} showResult={subMode === "missed" ? true : isRevealed} />
          <NavButtons
            current={current} total={displayQs.length}
            onPrev={() => current > 0 && setCurrent(current - 1)}
            onNext={() => current < displayQs.length - 1 && setCurrent(current + 1)}
            extra={!isRevealed && answers[current] === undefined && subMode !== "missed" && (
              <button onClick={() => setRevealed(p => ({ ...p, [current]: true }))} style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Show Answer</button>
            )}
          />
        </div>
        <div style={{ background: "#fff", margin: "12px 16px 0", borderRadius: 14, boxShadow: "0 2px 12px rgba(30,58,95,0.06)", border: "1px solid #e2e8f0" }}>
          <DotNavigator total={QUESTIONS.length} current={current} answers={answers} onGo={setCurrent} />
        </div>
      </div>
    </div>
  );
};

// ─── TIMED QUIZ MODE ──────────────────────────────────────────────────────────
const QuizResults = ({ answers, timePerQ, timeTaken, onHome, onRestart }) => {
  const [tab, setTab] = useState("overview");
  const [reviewIdx, setReviewIdx] = useState(0);
  const total = QUESTIONS.length;
  const attempted = Object.keys(answers).length;
  const correct = Object.entries(answers).filter(([i, a]) => a === QUESTIONS[parseInt(i)].correct).length;
  const score = Math.round((correct / total) * 100);
  const pass = score >= 60;
  const missedQs = QUESTIONS.filter((q, i) => answers[i] !== undefined && answers[i] !== q.correct);
  const avgTime = attempted > 0 ? Math.round(Object.values(timePerQ).reduce((a, b) => a + b, 0) / attempted) : 0;

  const byType = {};
  Object.keys(TYPE_LABELS).forEach(type => {
    const qs = QUESTIONS.filter(q => q.type === type);
    const cor = qs.filter(q => answers[QUESTIONS.indexOf(q)] === q.correct);
    const ans = qs.filter(q => answers[QUESTIONS.indexOf(q)] !== undefined);
    byType[type] = { total: qs.length, answered: ans.length, correct: cor.length };
  });

  const TabBtn = ({ id, label }) => (
    <button onClick={() => setTab(id)} style={{ background: tab === id ? "#1e3a5f" : "transparent", color: tab === id ? "#fff" : "#64748b", border: tab === id ? "none" : "1px solid #e2e8f0", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{label}</button>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", padding: "20px 16px" }}>
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <div style={{ background: pass ? "linear-gradient(135deg,#1e3a5f,#2d5986)" : "linear-gradient(135deg,#7f1d1d,#991b1b)", borderRadius: 16, padding: "28px 24px", textAlign: "center", marginBottom: 16, color: "#fff", boxShadow: "0 6px 24px rgba(30,58,95,0.20)" }}>
          <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.7)", marginBottom: 6 }}>Vitta Finance Academy · Element 5</div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: 52, fontWeight: "bold", color: "#d4a017", lineHeight: 1 }}>{score}%</div>
          <div style={{ fontSize: 18, fontWeight: 600, marginTop: 6 }}>{pass ? "✓ Pass" : "✗ Below Passing"}</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", marginTop: 4 }}>{correct} / {total} correct · {fmtTime(timeTaken)} taken</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 16 }}>
          {[["Correct", correct, "#166534"], ["Incorrect", attempted - correct, "#991b1b"], ["Unanswered", total - attempted, "#92400e"], ["Avg/Q", `${avgTime}s`, "#1e40af"]].map(([label, value, color]) => (
            <div key={label} style={{ background: "#fff", borderRadius: 12, padding: "14px 10px", textAlign: "center", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(30,58,95,0.06)" }}>
              <div style={{ fontFamily: "Georgia,serif", fontSize: 22, fontWeight: "bold", color }}>{value}</div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500, marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          <TabBtn id="overview" label="📊 Overview" />
          <TabBtn id="breakdown" label="🔍 By Type" />
          <TabBtn id="review" label={`📝 Review (${missedQs.length})`} />
        </div>
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "20px", boxShadow: "0 2px 12px rgba(30,58,95,0.06)" }}>
          {tab === "overview" && (
            <div>
              <h3 style={{ fontFamily: "Georgia,serif", color: "#0f172a", margin: "0 0 16px", fontSize: 16 }}>Performance Overview</h3>
              {[["Score", score, "%", pass ? "linear-gradient(90deg,#166534,#22c55e)" : "linear-gradient(90deg,#991b1b,#ef4444)", "Passing: 60%"], ["Questions Attempted", Math.round((attempted / total) * 100), "%", "linear-gradient(90deg,#1e3a5f,#2d5986)", null]].map(([label, val, unit, grad, note]) => (
                <div key={label} style={{ marginBottom: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
                    <span style={{ color: "#64748b" }}>{label}</span>
                    <span style={{ fontWeight: 700 }}>{label === "Score" ? `${score}%` : `${attempted}/${total}`}</span>
                  </div>
                  <div style={{ height: label === "Score" ? 12 : 8, background: "#f1f5f9", borderRadius: 6, overflow: "hidden" }}>
                    <div style={{ height: "100%", borderRadius: 6, background: grad, width: `${val}%`, transition: "width 1s ease" }} />
                  </div>
                  {note && <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}><span style={{ fontSize: 11, color: "#94a3b8" }}>{note}</span></div>}
                </div>
              ))}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13, color: "#475569" }}>
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>⏱ Time Analysis</div>
                  <div>Total time: {fmtTime(timeTaken)}</div>
                  <div>Avg per question: {avgTime}s</div>
                  <div>Remaining: {fmtTime(QUIZ_DURATION - timeTaken)}</div>
                </div>
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>🎯 Accuracy</div>
                  <div>Attempted: {attempted}</div>
                  <div>Correct: {correct} ({attempted > 0 ? Math.round((correct / attempted) * 100) : 0}%)</div>
                  <div>Unanswered: {total - attempted}</div>
                </div>
              </div>
            </div>
          )}
          {tab === "breakdown" && (
            <div>
              <h3 style={{ fontFamily: "Georgia,serif", color: "#0f172a", margin: "0 0 16px", fontSize: 16 }}>Performance by Question Type</h3>
              {Object.entries(byType).map(([type, data]) => {
                const pct = data.answered > 0 ? Math.round((data.correct / data.answered) * 100) : 0;
                const c = TYPE_COLORS[type];
                return (
                  <div key={type} style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <TypeBadge type={type} />
                        <span style={{ fontSize: 12, color: "#64748b" }}>{data.correct}/{data.answered} of {data.total}</span>
                      </div>
                      <span style={{ fontFamily: "Georgia,serif", fontWeight: "bold", fontSize: 16, color: c.text }}>{pct}%</span>
                    </div>
                    <div style={{ height: 8, background: "#f1f5f9", borderRadius: 6, overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 6, background: c.text, width: `${pct}%`, transition: "width 1s ease" }} />
                    </div>
                  </div>
                );
              })}
              <div style={{ marginTop: 16, background: "#f8fafc", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#475569", border: "1px solid #e2e8f0" }}>
                <div style={{ fontWeight: 600, color: "#0f172a", marginBottom: 6 }}>📌 Study Focus</div>
                {Object.entries(byType).filter(([, d]) => d.answered > 0 && (d.correct / d.answered) < 0.6).map(([type]) => (
                  <div key={type} style={{ marginBottom: 4 }}>⚠️ <strong>{TYPE_LABELS[type]}</strong> — below 60% — recommend review</div>
                ))}
                {Object.entries(byType).every(([, d]) => d.answered === 0 || (d.correct / d.answered) >= 0.6) && <div>✓ All attempted categories are at or above 60%</div>}
              </div>
            </div>
          )}
          {tab === "review" && (
            <div>
              <h3 style={{ fontFamily: "Georgia,serif", color: "#0f172a", margin: "0 0 4px", fontSize: 16 }}>Incorrect Answers Review</h3>
              {missedQs.length === 0 ? (
                <div style={{ textAlign: "center", padding: "30px 0", color: "#64748b" }}>
                  <div style={{ fontSize: 36 }}>🎉</div>
                  <div style={{ marginTop: 10, fontWeight: 600 }}>No incorrect answers to review!</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>{reviewIdx + 1} of {missedQs.length} missed questions</div>
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", marginBottom: 14 }}>
                    <QuestionCard question={missedQs[reviewIdx]} selectedAnswer={answers[QUESTIONS.indexOf(missedQs[reviewIdx])]} onSelect={() => {}} showResult={true} />
                    <div style={{ padding: "0 24px 16px" }} />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => reviewIdx > 0 && setReviewIdx(reviewIdx - 1)} disabled={reviewIdx === 0} style={{ flex: 1, background: reviewIdx === 0 ? "#f1f5f9" : "#f8fafc", color: reviewIdx === 0 ? "#94a3b8" : "#1e3a5f", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 600, cursor: reviewIdx === 0 ? "default" : "pointer" }}>← Prev</button>
                    <button onClick={() => reviewIdx < missedQs.length - 1 && setReviewIdx(reviewIdx + 1)} disabled={reviewIdx === missedQs.length - 1} style={{ flex: 1, background: reviewIdx === missedQs.length - 1 ? "#f1f5f9" : "linear-gradient(135deg,#1e3a5f,#2d5986)", color: reviewIdx === missedQs.length - 1 ? "#94a3b8" : "#fff", border: "none", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 600, cursor: reviewIdx === missedQs.length - 1 ? "default" : "pointer" }}>Next →</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          <button onClick={onRestart} style={{ flex: 1, minWidth: 140, background: "linear-gradient(135deg,#1e3a5f,#2d5986)", color: "#fff", border: "none", borderRadius: 10, padding: "13px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>🔄 New Quiz</button>
          <button onClick={onHome} style={{ flex: 1, minWidth: 140, background: "#fff", color: "#1e3a5f", border: "1px solid #e2e8f0", borderRadius: 10, padding: "13px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>← Home</button>
        </div>
      </div>
    </div>
  );
};

const TimedQuizMode = ({ onHome }) => {
  const [phase, setPhase] = useState("intro");
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(QUIZ_DURATION);
  const [timePerQ, setTimePerQ] = useState({});
  const [qStartTime, setQStartTime] = useState(null);
  const [flagged, setFlagged] = useState(new Set());
  const timerRef = useRef(null);

  useEffect(() => {
    if (phase === "quiz") {
      setQStartTime(Date.now());
      timerRef.current = setInterval(() => {
        setTimeLeft(t => { if (t <= 1) { clearInterval(timerRef.current); setPhase("results"); return 0; } return t - 1; });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [phase]);

  const handleSelect = (i) => {
    const elapsed = qStartTime ? Math.round((Date.now() - qStartTime) / 1000) : 0;
    setTimePerQ(p => ({ ...p, [current]: elapsed }));
    setQStartTime(Date.now());
    setAnswers(p => ({ ...p, [current]: i }));
  };

  const goTo = (idx) => {
    const elapsed = qStartTime ? Math.round((Date.now() - qStartTime) / 1000) : 0;
    setTimePerQ(p => ({ ...p, [current]: (p[current] || 0) + elapsed }));
    setQStartTime(Date.now());
    setCurrent(idx);
  };

  const timeTaken = QUIZ_DURATION - timeLeft;
  const isWarning = timeLeft < 600, isCritical = timeLeft < 120;

  if (phase === "intro") return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 16, maxWidth: 480, width: "100%", padding: "36px 32px", boxShadow: "0 4px 24px rgba(30,58,95,0.10)", border: "1px solid #e2e8f0", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⏱</div>
        <h2 style={{ fontFamily: "Georgia,serif", color: "#0f172a", margin: "0 0 12px", fontSize: 22 }}>Timed Quiz — Element 5</h2>
        <p style={{ color: "#475569", fontSize: 14, lineHeight: 1.7, margin: "0 0 24px" }}>100 questions · 75 minutes · Simulates real CIRO exam conditions. No feedback until submission. Full diagnostics at the end.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 24 }}>
          {[["📋", "100 Questions"], ["⏱", "75 Minutes"], ["🚩", "Flag for Review"], ["📊", "Full Diagnostics"]].map(([icon, label]) => (
            <div key={label} style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 10px", border: "1px solid #e2e8f0", fontSize: 13, color: "#334155" }}>
              <span style={{ fontSize: 18 }}>{icon}</span>
              <div style={{ marginTop: 4, fontWeight: 600 }}>{label}</div>
            </div>
          ))}
        </div>
        <button onClick={() => setPhase("quiz")} style={{ width: "100%", background: "linear-gradient(135deg,#1e3a5f,#2d5986)", color: "#fff", border: "none", borderRadius: 10, padding: "14px 20px", fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(30,58,95,0.25)", marginBottom: 10 }}>Start Timed Quiz →</button>
        <button onClick={onHome} style={{ background: "none", border: "1px solid #e2e8f0", color: "#64748b", borderRadius: 10, padding: "10px 20px", fontSize: 13, cursor: "pointer", width: "100%" }}>← Back to Home</button>
      </div>
    </div>
  );

  if (phase === "results") return <QuizResults answers={answers} timePerQ={timePerQ} timeTaken={timeTaken} onHome={onHome} onRestart={() => { setAnswers({}); setTimePerQ({}); setTimeLeft(QUIZ_DURATION); setCurrent(0); setFlagged(new Set()); setPhase("intro"); }} />;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 20px", position: "sticky", top: 0, zIndex: 10, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <VittaLogo />
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#64748b" }}>{Object.keys(answers).length}/100 answered</span>
          <div style={{ background: isCritical ? "#fee2e2" : isWarning ? "#fef3c7" : "#f0fdf4", border: `1.5px solid ${isCritical ? "#991b1b" : isWarning ? "#d97706" : "#166534"}`, borderRadius: 10, padding: "6px 14px", fontFamily: "monospace", fontSize: 18, fontWeight: "bold", color: isCritical ? "#991b1b" : isWarning ? "#92400e" : "#166534" }}>
            ⏱ {fmtTime(timeLeft)}
          </div>
          <button onClick={() => { clearInterval(timerRef.current); setPhase("results"); }} style={{ background: "#1e3a5f", color: "#d4a017", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Submit</button>
        </div>
      </div>
      <ProgressBar current={current + 1} total={QUESTIONS.length} answered={Object.keys(answers).length} />
      <div style={{ flex: 1, maxWidth: 760, margin: "0 auto", width: "100%", paddingBottom: 20 }}>
        <div style={{ background: "#fff", margin: "16px 16px 0", borderRadius: 14, boxShadow: "0 2px 12px rgba(30,58,95,0.08)", border: "1px solid #e2e8f0" }}>
          <QuestionCard question={QUESTIONS[current]} selectedAnswer={answers[current]} onSelect={handleSelect} showResult={false} />
          <NavButtons
            current={current} total={QUESTIONS.length}
            onPrev={() => current > 0 && goTo(current - 1)}
            onNext={() => current < QUESTIONS.length - 1 && goTo(current + 1)}
            extra={
              <button onClick={() => setFlagged(p => { const n = new Set(p); n.has(current) ? n.delete(current) : n.add(current); return n; })} style={{ background: flagged.has(current) ? "#fef3c7" : "#f8fafc", color: flagged.has(current) ? "#92400e" : "#64748b", border: flagged.has(current) ? "1px solid #fde68a" : "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", fontSize: 12, cursor: "pointer" }}>
                🚩 {flagged.has(current) ? "Flagged" : "Flag"}
              </button>
            }
          />
        </div>
        <div style={{ background: "#fff", margin: "12px 16px 0", borderRadius: 14, boxShadow: "0 2px 12px rgba(30,58,95,0.06)", border: "1px solid #e2e8f0" }}>
          <DotNavigator total={QUESTIONS.length} current={current} answers={answers} onGo={goTo} />
        </div>
      </div>
    </div>
  );
};

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("home");
  if (screen === "practice") return <PracticeMode onHome={() => setScreen("home")} />;
  if (screen === "quiz") return <TimedQuizMode onHome={() => setScreen("home")} />;
  return <HomeScreen onMode={setScreen} />;
}
